import { Box, Heading, Paragraph } from '@zillow/constellation-10';

import { formatAddress } from '../../../utils';

import { PROPERTY_TYPES } from '../../../constants';

import PropertyUnitDisplay from '../components/PropertyUnitDisplay';

import { ListingActivationTerms } from '../../../index';
import { StaticMap } from '../../../index';

import type { ReactElement } from 'react';
import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { AddressComponents } from '../../../types';

const { MULTIPLEX } = PROPERTY_TYPES;

type Props = {
  address: AddressComponents;
  propertyType: string;
  numUnits: string;
  showImpreciseLocationAlert?: boolean;
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
  isScreenLgGTE: boolean;
};

const ReviewPropertyInfoStep = ({
  address,
  propertyType,
  numUnits,
  showImpreciseLocationAlert = false,
  user,
  isScreenLgGTE,
}: Props): ReactElement => {
  const isMultiplexType = propertyType === MULTIPLEX;

  const { formattedStreet, formattedCityStateZip } = formatAddress(address);

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '600' }}>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
        <Heading level={2} textStyle="body-lg-bold">
          Review your property information
        </Heading>
        <Paragraph>
          Make sure you’ve provided a USPS-validated address for the property. After confirming, you won’t be able to
          change the address or number of units.
        </Paragraph>
      </Box>

      <Box
        data-testid="review-listing-details"
        css={{
          display: 'flex',
          flexDirection: { base: 'column', lg: 'row' },
          alignItems: 'flex-start',
          gap: '600',
        }}
      >
        <Box css={{ flex: { lg: '1' }, display: 'flex', flexDirection: 'column', gap: '400' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
            <Paragraph>
              <strong>Street address</strong>
            </Paragraph>
            <Paragraph data-testid="review-address-street">{formattedStreet}</Paragraph>
            {formattedCityStateZip && <Paragraph data-testid="review-address-city">{formattedCityStateZip}</Paragraph>}
          </Box>

          {isMultiplexType ? (
            <>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
                <Paragraph>
                  <strong>Property type</strong>
                </Paragraph>
                <Paragraph data-testid="review-property-type">{propertyType}</Paragraph>
              </Box>
              <PropertyUnitDisplay propertyType={propertyType} unit={address.unit ?? ''} numUnits={numUnits} />
            </>
          ) : (
            <>
              <PropertyUnitDisplay propertyType={propertyType} unit={address.unit ?? ''} numUnits={numUnits} />
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
                <Paragraph>
                  <strong>Property type</strong>
                </Paragraph>
                <Paragraph data-testid="review-property-type">{propertyType}</Paragraph>
              </Box>
            </>
          )}
        </Box>

        <Box
          css={{
            borderRadius: 'obj.default',
            border: '1px solid',
            borderColor: 'border.subtle',
            overflow: 'hidden',
          }}
        >
          <StaticMap
            address={[formattedStreet, formattedCityStateZip].filter(Boolean).join(', ')}
            showImpreciseLocationAlert={showImpreciseLocationAlert}
            user={user}
            isScreenLgGTE={isScreenLgGTE}
          />
        </Box>
      </Box>

      <ListingActivationTerms actionText="By confirming" />
    </Box>
  );
};

export default ReviewPropertyInfoStep;
